import { autoinject, transient } from "aurelia-dependency-injection";
import { Claims } from "../../../../../constants/authentication/claims";
import { HistoryHelper } from "../../../../../helpers/history-helper";
import { MobileHelper } from "../../../../../helpers/mobile-helper";
import { DataGridColumn, DataType, ItemSource, QueryFilter, SourceType } from "../../../../../models/queries/queries.models";
import { RightShipInspectionStatusEnum } from "../../../../../models/rightship-inspections/rightship-inspections.models";
import { TasksConstants } from "../../../../../models/tasks/task.model";
import { UserClaimsService } from "../../../../../services/authentication/user-claims.service";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { ClipboardCopyService } from "../../../../../utilities/clipboard-copy-service";
import { FromNowValueConverter } from "../../../../components/converters/date/date-format";

@autoinject()
@transient()
export class InspectorDialog  {
    isRightshipUser: boolean = false;

    model: {mobileNumber:string, email:string, inspectionCompany:string, inspectorName:string, inspectorId: number};

    historyItemSource:ItemSource;
    upcomingItemSource:ItemSource;

    activate(model){
        this.model = model;
        this.historyItemSource = {
            sourceType: SourceType.ODataUrl,
            source: "odata/RightShipInspections",
            columns: InspectorDialog.getColumns(true, true, this.isRightshipUser),
            initialFilters: [
                QueryFilter.getQuery("inspectionDate", new Date(), "le", DataType.Date, true),
                QueryFilter.getQuery("inspectorId", model.inspectorId, "eq", DataType.Number, true),
            ]
        };
        this.upcomingItemSource = {
            sourceType: SourceType.ODataUrl,
            source: "odata/RightShipInspections",
            columns: InspectorDialog.getColumns(true, true, this.isRightshipUser),
            initialFilters: [
                QueryFilter.getQuery("statusId", RightShipInspectionStatusEnum.Cancelled, "ne", DataType.Number, true),
                QueryFilter.getQuery("statusId", RightShipInspectionStatusEnum.Acceptable, "ne", DataType.Number, true),
                QueryFilter.getQuery("statusId", RightShipInspectionStatusEnum.Unacceptable, "ne", DataType.Number, true),
                QueryFilter.getQuery("inspectionDate", new Date(), "gt", DataType.Date, true),
                QueryFilter.getQuery("inspectorId", model.inspectorId, "eq", DataType.Number, true),
            ]
        };
    }

    pageCountSet: any[] = [
        { label: "5", value: 5 }
    ];


    constructor(private clipboardCopyService:ClipboardCopyService, private dialog:DialogClientService, private userClaimsService: UserClaimsService){
        this.isRightshipUser = this.userClaimsService.hasPermission(Claims.RightShipUser)
    }
    
    close(){
        this.dialog.hide();
    }

    copy(str: string) {
        this.clipboardCopyService.copy(str);
    }


    public static getColumns(openInANewTab: boolean, fromVet: boolean, rightshipUser: boolean) {
        const columns: DataGridColumn[] = [];
        const newTab = openInANewTab ? 'target="_blank"' : '';
        columns.push(
            <DataGridColumn>{
                columnOrder: 1,
                sortOrder: 1,
                dataType: DataType.DateTime,
                isSortable: true,
                name: 'inspectionDate',
                title: 'Inspection Date',
                secondParameter: 'MMMM Do YYYY'
            },
            <DataGridColumn>{
                columnOrder: 2,
                sortOrder: 2,
                dataType: DataType.CustomTemplate,
                instancePerRow: true,
                name: 'customerName',
                title: 'Customer/Inspection ID',
                value:
                    '<div class="reviewer" if.bind="item.businessUnitName">' +
                    '<span class="name">${item.customerName} - ${item.businessUnitName}' +
                    '</span>' +
                    '</span>' +
                    '<span class="type" if.bind="item.rightShipInspectionId">Inspection ID #${item.rightShipInspectionId}</span>' +
                    '</div>' +
                    '<div class="reviewer" if.bind="!item.businessUnitName">' +
                    '<span class="type" if.bind="item.rightShipInspectionId">RightShip IT / Inspection ID #${item.rightShipInspectionId}</span>' +
                    '</div>',
                isSortable: true,
                parameter: {
                    item: null,
                }
            },
            <DataGridColumn>{
                columnOrder: 3,
                sortOrder: 3,
                dataType: DataType.CustomTemplate,
                name: 'vesselName',
                title: MobileHelper.isMobileDevice() && window.innerWidth < 1024 ? "" : "Name",
                value: '<div class="column">' +
                    '<div class="row align-items-middle">' +
                    '<a class="link" href="/insp/${item.rightShipInspectionId}?source=activities" ' + newTab + ' mousedown.trigger="itemClick()">${item.vesselName}</a>' +
                    '<svg class="rs-badge" viewBox="0 0 30 30" if.bind="item.isRightShipMember && isRightshipUser">' +
                    '<g>' +
                    '<path fill="#003745" d="M2,0h26c1.1,0,2,0.9,2,2v26c0,1.1-0.9,2-2,2H2c-1.1,0-2-0.9-2-2V2C0,0.9,0.9,0,2,0z"/>' +
                    '<path fill="#FF5F1B" d="M2,0h1.5c0.5,0,1,0.2,1.4,0.6l15.5,14.8l0,0L4.9,29.5C4.5,29.8,4,30,3.5,30H2c-1.1,0-2-0.9-2-2V2C0,0.9,0.9,0,2,0z"/>' +
                    '</g>' +
                    '</svg>' +
                    '</div>' +
                    '<span class="info-text" if.bind="item.internationalMaritimeOrganizationNumber">IMO ${item.internationalMaritimeOrganizationNumber}</span>' +
                    '<span class="info-text" if.bind="item.vehicleIdentificationNumber">VIN ${item.vehicleIdentificationNumber}</span>' +
                    '<span class="info-text" if.bind="item.europeanNumberOfIdentification">ENI ${item.europeanNumberOfIdentification}</span></div>',
                instancePerRow: true,
                isSortable: true,
                parameter: {
                    item: null,
                    isRightshipUser: rightshipUser,
                    itemClick: () => {
                        HistoryHelper.addState(TasksConstants.historyTaskTabKey, TasksConstants.INSPListName);

                        return true;
                    }
                }
            },
            <DataGridColumn>{
                columnOrder: 4,
                sortOrder: 4,
                dataType: DataType.Default,
                isSortable: true,
                name: 'portName',
                title: 'Port Name',
                valueConverter: (value) => value == null? 'Unknown Port': value
            },
            <DataGridColumn>{
                columnOrder: 8,
                sortOrder: 1,
                dataType: DataType.CustomTemplate,
                instancePerRow: true,
                isSortable: true,
                name: 'status',
                title: 'Status',
                value:
                    '<div class="status-badge-value" if.bind="item.status">' +
                    '<div class="row nowrap align-items-middle">' +
                    '<div class="status-badge-dot"></div>' +
                    '<span>${item.status}</span>' +
                    '<div class="info" if.bind="item.awaitingResponseStatusReason && item.status.toLowerCase() != \'unacceptable\' && item.status.toLowerCase() != \'acceptable\'">' +
                    '<i class="icon-info material-icons">info_outline</i>' +

                    //Tooltip
                    '<div class="status-tooltip">' +
                    '<div class="wrap-tooltip">' +
                    '<p class="desc">${item.awaitingResponseStatusReason}</p>' +
                    '</div>' +
                    '</div>' +
                    '</div>' +
                    '</div>' +
                    '<span class="status-date">${getDateToString(item.statusDate)}</span>' +
                    '</div>',
                getCellClass: (item: { status: string }) => {
                    return `status-badge ${item.status.toLowerCase().replace(/[^A-z0-9]+/g, '-')}`;
                },
                parameter: {
                    item: null,
                    getDateToString: (date: Date | string) => this.getDateToString(date)
                }
            });
        return columns;
    }

    public static getDateToString(date: Date | string): string {
        if (!date)
            return 'N/A'

        let val: Date;

        if (typeof date == 'string')
            val = new Date(date);
        else
            val = date

        if (val.getUTCMonth() == 0 && val.getUTCDay() == 1 && val.getUTCFullYear() == 1900)
            return 'N/A';

        return new FromNowValueConverter().toView(val);
    }
}